import React from 'react';
import { useSelector } from 'react-redux';
import camelCase from 'lodash/camelCase';
import { uniqWith } from 'rambdax';
import ConstituentQueryTypeFields from '@/constants/constituents/ConstituentQueryTypeFields';
import SortOrder from '@/constants/SortOrder';
import useDebouncedEffect from '@/lib/hooks/useDebouncedEffect';
import { updateConstituent } from '@/services/constituentServices';
import { useLifecycleBoardContext } from '../../components/LifecycleBoardContext';
import { fetchCardsByColumn } from '../LifecycleBoard.services';

const CARDS_PER_PAGE = 20;

export const sortAndFlattenCards = (items, sort, order) => {
  // The API returns constituents properly sorted, however in the case where a
  // user updates a card's category, we still need to manage that
  // state and sorting on the front-end to avoid potential lags with Elastic Search.
  const predicate = (x, y) => x.constituentId === y.constituentId;
  const sortedUniqueCards = uniqWith(
    predicate,
    items.flat().filter(Boolean),
  ).sort((a, b) => {
    const left = a[camelCase(sort)]?.toLowerCase() || '';
    const right = b[camelCase(sort)]?.toLowerCase() || '';
    if (left > right) {
      return order === SortOrder.ASC ? 1 : -1;
    }
    if (left < right) {
      return order === SortOrder.ASC ? -1 : 1;
    }
    return 0;
  });
  return sortedUniqueCards;
};

const useLifecycleCards = ({ columnId = null, page = 0 }) => {
  const { getColumn, filters, updateColumn } = useLifecycleBoardContext();
  const { ownerOrgId } = useSelector((state) => ({
    ownerOrgId: state.authentication?.ownerOrgId || '',
  }));
  let total = null;
  let items = [];
  if (columnId !== null) {
    const data = getColumn(columnId);
    total = data.total;
    items = data.items;
  }
  const [isLoading, setIsLoading] = React.useState(false);

  // Reset the cards when parameters other than pagination changes.
  React.useEffect(() => {
    if (columnId === null) return;
    updateColumn(columnId, { items: [], total: 0 });
    setIsLoading(true);
  }, [columnId, filters, ownerOrgId]);

  // Fetch cards.
  useDebouncedEffect(
    () => {
      if (!ownerOrgId || columnId === null) return;
      (async () => {
        try {
          const {
            userIds,
            filters: quickFilters,
            advancedFilters,
            ...rest
          } = filters;
          let users = null;
          let hasAssignees = null;
          switch (userIds) {
            case 'all':
              break;
            case 'assigned':
              hasAssignees = true;
              break;
            case 'unassigned':
              hasAssignees = false;
              break;
            default:
              users = [userIds];
              break;
          }
          const params = {
            ...rest,
            filters: [...quickFilters, ...advancedFilters],
            pageSize: CARDS_PER_PAGE,
            pageFrom: CARDS_PER_PAGE * page,
            queryType: ConstituentQueryTypeFields.V3,
            userIds: users,
            hasAssignees,
          };

          const newItems = [...items];
          const { data } = await fetchCardsByColumn({
            columnId,
            params,
            ownerOrgId,
          });
          newItems[page] = data.constituents;
          updateColumn(columnId, { total: data.totalHits, items: newItems });
        } catch (e) {
          // do something with error here?
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [columnId, filters, ownerOrgId, page],
    500,
  );

  const updateCard = React.useCallback(
    (constituentId) =>
      async (update = {}) => {
        const { data } = await updateConstituent({
          constituentId,
          data: update,
          ownerOrgId,
        });
        const newItems = items.map((group) =>
          group.map((item) =>
            item.constituentId === constituentId ? data.constituent : item,
          ),
        );
        updateColumn(columnId, { total, items: newItems });
      },
    [items, ownerOrgId, total],
  );

  const cards = React.useMemo(
    () => sortAndFlattenCards(items, filters.sortBy, filters.sortOrder),
    [getColumn, items],
  );
  return { cards, filters, isLoading, total, updateCard };
};

export default useLifecycleCards;
